import { useState, useEffect, useRef } from 'react';
import KeycloakService from '../services/KeycloakService';

interface ConnectOptions {
  // Send the user's Keycloak token in the first message, for routes that check
  // it (pyapi's AI-workflow trial gate). A browser can't put an Authorization
  // header on a websocket, so it travels in the body; pyapi strips it on arrival.
  sendAccessToken?: boolean;
}

export const useWebSocketFetcher = (wsUrl: string) => {
  const [ streamData, setStreamData ] = useState<string[]>([]);
  const [ isStreaming, setIsStreaming ] = useState<boolean>(false);
  const wsRef = useRef<WebSocket | null>(null);
  // Synchronous guard, checked/set BEFORE any state update. `isStreaming` is
  // `useState`, so two connectWebSocket calls fired in the same tick (e.g. a
  // double-dispatched button action) can both read the stale `false` value
  // before React commits the first call's `setIsStreaming(true)` — opening
  // two live connections for what was meant to be a single turn, which can
  // make the backend double-process it (duplicate streamed narration for one
  // phase, e.g. Schedule B narrating twice in a row).
  const isStreamingRef = useRef(false);

  const connectWebSocket = (
    endpoint: string,
    body: Record<string, any>,
    options: ConnectOptions = {},
  ) => {
    if (isStreamingRef.current) {
      return;
    }
    isStreamingRef.current = true;

    setIsStreaming(true);
    setStreamData([]);

    const url = `${wsUrl}${endpoint}`; // Ensure the WebSocket endpoint is correct
    const ws = new WebSocket(url);

    wsRef.current = ws;

    ws.onopen = async () => {
      let payload = body;
      if (options.sendAccessToken) {
        try {
          await KeycloakService.updateToken();
        } catch {
          // Send what we have; pyapi answers an expired token with a sign-in message.
        }
        payload = { ...body, access_token: KeycloakService.getToken() };
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(payload));
      }
    };

    ws.onmessage = (event) => {
      setStreamData((prev) => [ ...prev, event.data ]);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      // Only the socket this handler was registered for may clear the shared
      // guard/state. closeWebSocket() clears wsRef synchronously and calls
      // close() on the old socket, but its own onclose event fires later,
      // asynchronously — if a new connectWebSocket() call has already started
      // a replacement socket by then, this stale handler must not stomp the
      // new one's "streaming" state.
      if (wsRef.current === ws) {
        wsRef.current = null;
        isStreamingRef.current = false;
        setIsStreaming(false);
      }
    };
  };

  const closeWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
    } else {
      isStreamingRef.current = false;
      setIsStreaming(false);
    }
  };

  const clearStreamData = () => {
    setStreamData([]);
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      closeWebSocket();
    };
  }, []);

  return {
    streamData: streamData,
    isStreaming: isStreaming,
    connectWebSocket: connectWebSocket,
    closeWebSocket: closeWebSocket,
    clearStreamData: clearStreamData,
  };
};
